import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useMultiOrder } from './use-order-drawer-section';
import { MultipleOrders } from '../../../components/orders-drawer/sections/multi-order-header';
import { useEnableBulkRatesOnSuperSelect } from '../../../feature-flags/hooks/use-enable-bulk-rates-on-super-select';
import { useEnableBulkRefreshRatesOnSuperSelect } from '../../../feature-flags/hooks/use-enable-bulk-refresh-rates-on-super-select';
import { useOrdersGridRequestFilters } from '../../../fulfillment-planner/rate-recommendation/recommendation/use-rate-recommendations';
import { bulkRateSummary } from '../../../store/bulk/bulk.api';
import * as OrderGridHeaderActions from '../../../store/orders/grid/orders.grid.header.actions';

export const MultiOrderHeader = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [totalRate, setTotalRate] = useState({
    value: 0,
    errors: [],
    isMissingCostReviewData: 0,
  });
  const [isLoadingRates, setIsLoadingRates] = useAsyncSafeState(false);

  const { ordersCount, fulfillmentPlanIds, marketplaceId, isSuperSelect, isEligibleGridStatus, isRateJobRunning } = useMultiOrder(pathname);

  const gridApiFilters = useOrdersGridRequestFilters(undefined, pathname);
  const enableBulkRatesOnSuperSelect = useEnableBulkRatesOnSuperSelect();
  const enableBulkRefreshRatesOnSuperSelect = useEnableBulkRefreshRatesOnSuperSelect();

  useEffect(() => {
    // There is a brief period where the rate job has finished, but we don't have the new rates yet
    // So set isLoadingRates to true when job starts, then set it to false when we get the bulkRateSummary response
    if (isRateJobRunning) {
      setIsLoadingRates(true);
    }
  }, [isRateJobRunning, setIsLoadingRates]);

  useEffect(() => {
    const getBulkRateSummary = async () => {
      const response = await bulkRateSummary(gridApiFilters);
      const { totalRate: total, isMissingCostReviewData, errors } = await response;
      setTotalRate((previous) => ({
        ...previous,
        ...total,
        isMissingCostReviewData,
        errors,
      }));
    };
    if (enableBulkRatesOnSuperSelect && isEligibleGridStatus && !isRateJobRunning) {
      setIsLoadingRates(true);

      getBulkRateSummary()
        .catch(() => {
          setTotalRate((previous) => ({
            ...previous,
            value: 0,
            errors: [],
            isMissingCostReviewData: 0,
          }));
        })
        .finally(() => {
          setIsLoadingRates(false);
        });
    }
  }, [enableBulkRatesOnSuperSelect, gridApiFilters, isEligibleGridStatus, isRateJobRunning, setIsLoadingRates]);

  const onGetRates = () => {
    dispatch(OrderGridHeaderActions.orderGridGetRates(gridApiFilters));
  };

  return (
    <MultipleOrders
      disableRateRefresh={!isEligibleGridStatus || (isSuperSelect && !enableBulkRefreshRatesOnSuperSelect)}
      fulfillmentPlanIds={fulfillmentPlanIds}
      isEligibleForBulkRecommendedRates={isEligibleGridStatus}
      isLoadingRates={isLoadingRates}
      marketplaceId={marketplaceId}
      onGetRates={onGetRates}
      ordersCount={ordersCount}
      ordersRate={totalRate}
    />
  );
};
